import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How serverless beats servers",
  "description": "",
  "published": "2020-01-28T00:00:00.000Z",
  "redirect_from": ["/blog/how-serverless-beats-servers/swizec/9311"],
  "categories": "Back End, Technical",
  "hero": "./img/techletter.app-screenshot-1580225198153.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`You're building an app. It needs to store some data. What do you do?`}</p>
    <p>{`Easy.`}</p>
    <p>{`Put it in local storage.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=localStorage.setItem(%0A%09'item'%2C%20%0A%09JSON.stringify(%7B%22property1%22%3A%202%2C%20%22property2%22%3A%203%7D)%0A)"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "518px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "46.63677130044843%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'186\\'%20viewBox=\\'0%200%20400%20186\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M26%2038c-2%202-2%20109%200%20111s346%202%20348%200%202-109%200-111-346-2-348%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6127eb3c0db702081c8cb311683c8ae3/ca0a1/techletter.app-screenshot-1580225198153.webp 223w", "/static/6127eb3c0db702081c8cb311683c8ae3/75680/techletter.app-screenshot-1580225198153.webp 445w", "/static/6127eb3c0db702081c8cb311683c8ae3/c29d2/techletter.app-screenshot-1580225198153.webp 518w"],
                "sizes": "(max-width: 518px) 100vw, 518px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6127eb3c0db702081c8cb311683c8ae3/e92b6/techletter.app-screenshot-1580225198153.png 223w", "/static/6127eb3c0db702081c8cb311683c8ae3/e66bf/techletter.app-screenshot-1580225198153.png 445w", "/static/6127eb3c0db702081c8cb311683c8ae3/6b9fd/techletter.app-screenshot-1580225198153.png 518w"],
                "sizes": "(max-width: 518px) 100vw, 518px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/6127eb3c0db702081c8cb311683c8ae3/6b9fd/techletter.app-screenshot-1580225198153.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`You can read `}<inlineCode parentName="p">{`'item'`}</inlineCode>{` next time users visit your app. Yay.`}</p>
    <p>{`Unless they're on a different computer. Or browser. Or need to share between users.`}</p>
    <p><strong parentName="p">{`Time to add a backend. How hard can it be?`}</strong></p>
    <p>{`Start a RubyOnRails project, create a model for your data, add routes for reading and writing, add some glue code in your controller, install the JBuilder gem, write a JSON view, create a migration file, run the Postgres database locally, start your Rails app and you're ready to test.`}</p>
    <p>{`Then you go to AWS, create a new EC2 instance, pick from a billion Linux distributions, set up an HTTP server like Nginx, run your Rails app, add a cronjob to restart the app when it dies, buy a new domain on Namecheap, set up its DNS servers, wait a few hours, point the domain to your server ...`}</p>
    <p>{`... and you still need the production database.`}</p>
    <p>{`You now have a virtual server and database running 24/7. Unless they die.`}</p>
    <p>{`You're in charge of keeping them up, maintaining configuration, waking up at 3am if something goes wrong, and paying for every second of every day `}<em parentName="p">{`even if nobody uses your app`}</em>{`.`}</p>
    <p>{`Just to store some data.`}</p>
    <p><img parentName="p" {...{
        "src": "/5092fcfc541191629a98a6ca40bf8f79/media-jSxK33dwEMbkY-giphy.gif",
        "alt": "kill_me giphy"
      }}></img></p>
    <p>{`Yes, services like Heroku make it easier. Docker gives you pre-configured Linux machines, and Kubernetes does whatever Kubernetes does. Helps keep your servers alive? Maybe improve deployments?`}</p>
    <p>{`Did I mention you still have to host your webapp somewhere?`}</p>
    <p>{`You'll need a static file server, some CDN setup, optimize JavaScript builds for production ... you want your webapp to be fast. Can't have it hitting Rails for every little CSS file.`}</p>
    <p>{`It's a lot of work.`}</p>
    <p>{`Weeks of engineering time at my day job. 😅`}</p>
    <p>{`There's a better way, my friend. Serverless for the backend, JAMStack for the frontend.`}</p>
    <h2 {...{
      "id": "serverless-for-the-backend"
    }}>{`Serverless for the backend`}</h2>
    <p>{`The simplest serverless backends look just like functions.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=export%20const%20handler%20%3D%20async%20(event%3A%20APIGatewayEvent)%20%3D%3E%20%7B%0A%09return%20%7B%0A%09%09statusCode%3A%20200%2C%0A%09%09body%3A%20%22Hello%20%F0%9F%91%8B%22%0A%09%7D%3B%0A%7D%3B"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "566px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "48.87892376681614%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'196\\'%20viewBox=\\'0%200%20400%20196\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M24%2035c-2%201-3%20122-1%20125l2%203h350l2-3c2-3%201-124-1-125H24\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6a4846adc80d288775b0a934173ee693/ca0a1/techletter.app-screenshot-1580225198140.webp 223w", "/static/6a4846adc80d288775b0a934173ee693/75680/techletter.app-screenshot-1580225198140.webp 445w", "/static/6a4846adc80d288775b0a934173ee693/3161c/techletter.app-screenshot-1580225198140.webp 566w"],
                "sizes": "(max-width: 566px) 100vw, 566px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6a4846adc80d288775b0a934173ee693/e92b6/techletter.app-screenshot-1580225198140.png 223w", "/static/6a4846adc80d288775b0a934173ee693/e66bf/techletter.app-screenshot-1580225198140.png 445w", "/static/6a4846adc80d288775b0a934173ee693/6fe44/techletter.app-screenshot-1580225198140.png 566w"],
                "sizes": "(max-width: 566px) 100vw, 566px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/6a4846adc80d288775b0a934173ee693/6fe44/techletter.app-screenshot-1580225198140.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`That's all it takes. That's a server 😍`}</p>
    <p>{`I like to use TypeScript for the backend so I make fewer mistakes. That's where the `}<inlineCode parentName="p">{`APIGatewayEvent`}</inlineCode>{` part comes from.`}</p>
    <p>{`We `}<a parentName="p" {...{
        "href": "https://serverlessreact.dev/#how-graphql-blows-rest-out-of-the-water"
      }}>{`talked about GraphQL`}</a>{` last week, so let's see what it takes to build a basic GraphQL API with serverless.`}</p>
    <p>{`You need just 2 files:`}</p>
    <ul>
      <li parentName="ul">{`an infrastructure definition`}</li>
      <li parentName="ul">{`a lambda function that creates your GraphQL server`}</li>
    </ul>
    <h3 {...{
      "id": "infrastructure-as-code"
    }}>{`infrastructure as code`}</h3>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=yaml&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=service%3A%20smol-graphql%0A%0A%23%20Define%20provider%20and%20runtime%0Aprovider%3A%0A%20%20%20%20name%3A%20aws%0A%20%20%20%20runtime%3A%20nodejs12.x%0A%20%20%20%20stage%3A%20dev%0A%20%20%20%20%23%20these%20are%20env%20vars%0A%20%20%20%20environment%3A%0A%20%20%20%20%20%20%20%20ITEM_TABLE%3A%20%24%7Bself%3Aservice%7D-items-%24%7Bself%3Aprovider.stage%7D%0A%20%20%20%20%23%20AWS%20is%20strict%20about%20permissions%20%3A)%0A%20%20%20%20iamRoleStatements%3A%0A%20%20%20%20%20%20%20%20-%20Effect%3A%20Allow%0A%20%20%20%20%20%20%20%20%20%20Action%3A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20-%20dynamodb%3AQuery%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20-%20dynamodb%3AScan%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20-%20dynamodb%3AGetItem%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20-%20dynamodb%3APutItem%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20-%20dynamodb%3AUpdateItem%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20-%20dynamodb%3ADeleteItem%0A%20%20%20%20%20%20%20%20%20%20Resource%3A%20%22arn%3Aaws%3Adynamodb%3A%24%7Bopt%3Aregion%2C%20self%3Aprovider.region%7D%3A*%3Atable%2F%24%7Bself%3Aprovider.environment.ITEM_TABLE%7D%22%0A%20%20%20%20%20%20%20%20%20%20%0A%23%20define%20a%20%2Fgraphql%20function%0Afunctions%3A%0A%20%20%20%20graphql%3A%0A%20%20%20%20%20%20%20%20handler%3A%20dist%2Fgraphql.handler%0A%20%20%20%20%20%20%20%20events%3A%0A%20%20%20%20%20%20%20%20%20%20%20%20-%20http%3A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20path%3A%20graphql%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20method%3A%20GET%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20cors%3A%20true%0A%20%20%20%20%20%20%20%20%20%20%20%20-%20http%3A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20path%3A%20graphql%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20method%3A%20POST%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20cors%3A%20true%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%23%20create%20a%20DynamoDB%20table%0Aresources%3A%0A%20%20%20%20Resources%3A%0A%20%20%20%20%20%20%20%20ItemsTable%3A%0A%20%20%20%20%20%20%20%20%20%20%20%20Type%3A%20%22AWS%3A%3ADynamoDB%3A%3ATable%22%0A%20%20%20%20%20%20%20%20%20%20%20%20Properties%3A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20AttributeDefinitions%3A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20-%20AttributeName%3A%20itemId%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20AttributeType%3A%20S%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20KeySchema%3A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20-%20AttributeName%3A%20itemId%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20KeyType%3A%20HASH%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20ProvisionedThroughput%3A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20ReadCapacityUnits%3A%201%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20WriteCapacityUnits%3A%201%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20TableName%3A%20%24%7Bself%3Aprovider.environment.ITEM_TABLE%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "890px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "109.86547085201795%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'439\\'%20viewBox=\\'0%200%20400%20439\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M13%2024c-2%2011-1%20395%200%20396%202%202%20372%202%20374%200%201-1%202-385%200-396v-5H13v5\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/de2f0aa96a82f1b38dc20562f220fddf/ca0a1/techletter.app-screenshot-1580225198202.webp 223w", "/static/de2f0aa96a82f1b38dc20562f220fddf/75680/techletter.app-screenshot-1580225198202.webp 445w", "/static/de2f0aa96a82f1b38dc20562f220fddf/8d1ba/techletter.app-screenshot-1580225198202.webp 890w", "/static/de2f0aa96a82f1b38dc20562f220fddf/a9a89/techletter.app-screenshot-1580225198202.webp 1024w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/de2f0aa96a82f1b38dc20562f220fddf/e92b6/techletter.app-screenshot-1580225198202.png 223w", "/static/de2f0aa96a82f1b38dc20562f220fddf/e66bf/techletter.app-screenshot-1580225198202.png 445w", "/static/de2f0aa96a82f1b38dc20562f220fddf/4ef49/techletter.app-screenshot-1580225198202.png 890w", "/static/de2f0aa96a82f1b38dc20562f220fddf/2bef9/techletter.app-screenshot-1580225198202.png 1024w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/de2f0aa96a82f1b38dc20562f220fddf/4ef49/techletter.app-screenshot-1580225198202.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`This might look scary, but imagine doing it by hand. Eeeevery time you want to make a small change.`}</p>
    <p>{`It's your whole infrastructure. Defines your runtime environment, API endpoints, even creates a database using DynamoDB. And it hooks everything up on its own.`}</p>
    <p>{`Your infrastructure becomes code.`}</p>
    <p>{`That way you always know what's running in production. Every branch of your project can use a different setup, every feature can add what it needs. Never mess around by hand ever again. ✌️`}</p>
    <p>{`We go into more detail in my new course, `}<a parentName="p" {...{
        "href": "https://serverlessreact.dev"
      }}>{`ServerlessReact.Dev`}</a></p>
    <h3 {...{
      "id": "a-small-graphql-server"
    }}>{`a small GraphQL server`}</h3>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20this%20is%20where%20we%20define%20the%20shape%20of%20our%20API%0Aconst%20schema%20%3D%20gql%60%0A%20%20type%20Item%20%7B%0A%20%20%20%20itemId%3A%20String%0A%20%20%20%20property1%3A%20String%0A%20%20%20%20property2%3A%20String%0A%20%20%7D%0A%20%20type%20Query%20%7B%0A%20%20%20%20item(itemId%3A%20String!)%3A%20Item%0A%20%20%7D%0A%20%20type%20Mutation%20%7B%0A%20%20%20%20createItem(property1%3A%20String%2C%20property2%3A%20String)%3A%20Item%0A%20%20%7D%0A%60%0A%0A%2F%2F%20this%20is%20where%20the%20shape%20maps%20to%20functions%0Aconst%20resolvers%20%3D%20%7B%0A%20%20Query%3A%20%7B%0A%20%20%20%20%2F%2F%20read%20an%20item%20from%20the%20database%20and%20return%0A%20%20%20%20item%3A%20async%20(_%3A%20any%2C%20%7B%20itemId%20%7D%3A%20ItemParams)%20%3D%3E%20%7B%0A%20%20%20%20%20%20const%20result%20%3D%20await%20getItem(%7B%0A%20%20%20%20%20%20%20%20TableName%3A%20process.env.ITEM_TABLE!%2C%0A%20%20%20%20%20%20%20%20Key%3A%20%7B%20itemId%3A%20itemId%20as%20string%20%7D%2C%0A%20%20%20%20%20%20%7D)%0A%20%20%20%20%20%20return%20result.Item%0A%20%20%20%20%7D%2C%0A%20%20%7D%2C%0A%20%20Mutation%3A%20%7B%0A%20%20%20%20%2F%2F%20write%20item%20to%20the%20database%0A%20%20%20%20createItem%3A%20async%20(_%3A%20any%2C%20%7B%20property1%2C%20property2%20%7D%3A%20ItemParams)%20%3D%3E%20%7B%0A%20%20%20%20%20%20const%20itemId%20%3D%20uuidv4()%0A%0A%20%20%20%20%20%20%2F%2F%20this%20is%20a%20little%20repetitive%2C%20but%20there's%20ways%20to%20improve%20%3A)%0A%20%20%20%20%20%20const%20result%20%3D%20await%20updateItem(%7B%0A%20%20%20%20%20%20%20%20TableName%3A%20process.env.ITEM_TABLE!%2C%0A%20%20%20%20%20%20%20%20Key%3A%20%7B%20itemId%20%7D%2C%0A%20%20%20%20%20%20%20%20UpdateExpression%3A%20%22SET%20property1%20%3D%20%3Aproperty1%2C%20property2%20%3D%20%3Aproperty2%22%2C%0A%20%20%20%20%20%20%20%20ExpressionAttributeValues%3A%20%7B%0A%20%20%20%20%20%20%20%20%20%20%22%3Aproperty1%22%3A%20property1%2C%0A%20%20%20%20%20%20%20%20%20%20%22%3Aproperty2%22%3A%20property2%2C%0A%20%20%20%20%20%20%20%20%7D%2C%0A%20%20%20%20%20%20%20%20ReturnValues%3A%20%22ALL_NEW%22%2C%0A%20%20%20%20%20%20%7D)%0A%0A%20%20%20%20%20%20return%20result.Attributes%0A%20%20%20%20%7D%2C%0A%20%20%7D%2C%0A%7D%0A%0A%2F%2F%20Create%20a%20GraphQL%20server%0Aconst%20server%20%3D%20new%20ApolloServer(%7B%20typeDefs%3A%20schema%2C%20resolvers%20%7D)%0A%0Aexport%20const%20handler%20%3D%20server.createHandler(%7B%0A%20%20cors%3A%20%7B%0A%20%20%20%20origin%3A%20%22*%22%2C%20%2F%2F%20for%20security%20in%20production%2C%20lock%20this%20to%20your%20real%20endpoints%0A%20%20%20%20credentials%3A%20true%2C%0A%20%20%7D%2C%0A%7D)"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "742px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "163.67713004484304%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'654\\'%20viewBox=\\'0%200%20400%20654\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M18%2028c-2%203-1%20599%200%20600%202%202%20362%202%20364%200%201-1%202-597%200-600-1-3-363-3-364%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/8c7a9be6a3f14f066f480b2aa48fbca9/ca0a1/techletter.app-screenshot-1580225198266.webp 223w", "/static/8c7a9be6a3f14f066f480b2aa48fbca9/75680/techletter.app-screenshot-1580225198266.webp 445w", "/static/8c7a9be6a3f14f066f480b2aa48fbca9/28367/techletter.app-screenshot-1580225198266.webp 742w"],
                "sizes": "(max-width: 742px) 100vw, 742px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/8c7a9be6a3f14f066f480b2aa48fbca9/e92b6/techletter.app-screenshot-1580225198266.png 223w", "/static/8c7a9be6a3f14f066f480b2aa48fbca9/e66bf/techletter.app-screenshot-1580225198266.png 445w", "/static/8c7a9be6a3f14f066f480b2aa48fbca9/0f2bc/techletter.app-screenshot-1580225198266.png 742w"],
                "sizes": "(max-width: 742px) 100vw, 742px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/8c7a9be6a3f14f066f480b2aa48fbca9/0f2bc/techletter.app-screenshot-1580225198266.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`And you're done.`}</p>
    <p>{`In those 50 lines of code, you've defined the shape of your API with a GraphQL schema, mapped it to resolver functions, and created a server. It even comes with a playground so you can test your API.`}</p>
    <p>{`Heck, here's a link 👉 `}<a parentName="p" {...{
        "href": "https://ix6nvocq5d.execute-api.us-east-1.amazonaws.com/dev/graphql"
      }}>{`try this basic API`}</a></p>
    <p><a parentName="p" {...{
        "href": "https://ix6nvocq5d.execute-api.us-east-1.amazonaws.com/dev/graphql"
      }}><img parentName="a" {...{
          "src": "/873dc6a87fe1a857f2d4c7114016523b/6S5Ujkr.gif",
          "alt": null
        }}></img></a></p>
    <p>{`GraphQL machinery handles data transformations and understanding queries. Serverless machinery sets up your servers, keeps them running, and exposes a URL to the world.`}</p>
    <p><strong parentName="p">{`Your frontend skills transfer directly to the backend.`}</strong>{` `}<em parentName="p">{`If you can write a JavaScript function, you can write your backend.`}</em>{` And that's amazing, my friend.`}</p>
    <p>{`You'll learn this in more detail with `}<a parentName="p" {...{
        "href": "https://serverlessreact.dev"
      }}>{`ServerlessReact.dev`}</a></p>
    <h2 {...{
      "id": "serverless-runs-only-when-it-needs-to"
    }}>{`Serverless runs only when it needs to`}</h2>
    <p>{`Serverless keeps your code ready, `}<em parentName="p">{`but not running`}</em>{`. You only pay what you use.`}</p>
    <p>{`That's why I'm so ready to host that example above and give you a link. Costs me nothing. Server only runs when you hit the link.`}</p>
    <p>{`You visit the URL and that's when the magic happens.`}</p>
    <p>{`Serverless wakes up a server, creates a new instance, routes the URL to your function, and calls your code. Code talks to a database, gets the data, and returns. Just like a normal function.`}</p>
    <p>{`All within milliseconds.`}</p>
    <p>{`👉 `}<strong parentName="p">{`simple servers, running simple functions; on demand`}</strong>{`. It's brilliant.`}</p>
    <h2 {...{
      "id": "jamstack-to-host-the-frontend"
    }}>{`JAMStack to host the frontend`}</h2>
    <p>{`That's your backend then. What about the webapp?`}</p>
    <p>{`Well, there's a bunch of startups solving that problem. The industry looked at how hard this was and thought `}<em parentName="p">{`"Nah, we can fix this"`}</em></p>
    <p>{`And they did!`}</p>
    <p>{`Netlify is great because it's popular. But Zeit's my favorite. Just because of how slick it is.`}</p>
    <p>{`Go into your webapp folder and run`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=null&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=now"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "154px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "121.42857142857144%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'486\\'%20viewBox=\\'0%200%20400%20486\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M90%20127c-7%205-6-3-7%20116%200%20121-1%20114%207%20119l4%203h106c121%200%20112%200%20116-9l2-4V244c-1-120%200-113-7-117-4-2-4-2-111-2H94l-4%202m97%2038c-10%202-15%2016-9%2024%209%2011%2027%205%2027-10%200-9-9-16-18-14m49%202c-12%206-10%2024%203%2027%205%200%208%200%2012-4%208-7%206-19-3-23-6-2-7-2-12%200M128%20277v10c0%208%200%209%202%209s2-1%202-6c0-8%201-10%204-11%203%200%204%201%204%209%200%207%201%2010%203%208%202-1%201-16-1-18s-12-2-14-1m23%201c-8%209%202%2024%2011%2017%203-3%204-13%201-17-3-3-9-3-12%200m16%201l2%208c2%2011%204%2012%207%206l3-5v-6c-2-1-4%202-4%205-1%204-3%203-3-1%200-5-2-10-4-10l-1%203m-14%202c-2%203-1%208%201%2011%204%205%2010-3%207-10-2-3-6-4-8-1\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6f9a2d5420ab0b7a55a44b0f163c92a8/95838/techletter.app-screenshot-1580225197780.webp 154w"],
                "sizes": "(max-width: 154px) 100vw, 154px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6f9a2d5420ab0b7a55a44b0f163c92a8/1f4ea/techletter.app-screenshot-1580225197780.png 154w"],
                "sizes": "(max-width: 154px) 100vw, 154px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/6f9a2d5420ab0b7a55a44b0f163c92a8/1f4ea/techletter.app-screenshot-1580225197780.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`That's it. You have a globally deployed webapp with a public URL, CDN configuration, static builds at deploy, and everything else a modern app needs.`}</p>
    <p><img parentName="p" {...{
        "src": "/0a803b79fe27b0dc90bb13572531cbda/media-7FyMQm2vBiTjG-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`Don't know about you, my friend, but this is the most excited I've been about web development since I discovered React in 2015.`}</p>
    <p>{`Cheers, ~Swizec`}</p>
    <p>{`PS: yes both Netlify and Zeit added support for cloud functions in the latter parts of 2019. As much as I love the bleeding edge, I think staying a year or two behind the curve is best. They both use AWS Lambda under the hood anyway :)`}</p>
    <p>{`PPS: don't forget, `}<a parentName="p" {...{
        "href": "https://serverlessreact.dev"
      }}>{`ServerlessReact.Dev`}</a>{` that teaches you this wonderful new stack in detail is $100 off just this week`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      